import React, { useContext } from 'react'

import classnames from 'classnames'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useRouteMatch } from 'react-router-dom'
import { useImmer } from 'use-immer'

import { api } from '../api'
import { Logger } from '../utils/Logger'
import { chatSocket } from '../utils/socket'
import { ChatBubble, ChatBubbleProps } from './ChatBubble'
import { CurrentUserContext } from './CurrentUserContext'
import { InputBox } from './InputBox'

export interface ChannelMessagesProps {
  channelDetails: Api_ChannelsDetails.RootObject
}

export const ChannelMessages: React.FC<ChannelMessagesProps> = ({
  channelDetails,
}) => {
  const isChannelConversation =
    useRouteMatch('/channels/:channelId')?.isExact === true

  const channel = channelDetails.channel
  const channelId = channel.id
  const [canLoadBefore, setCanLoadBefore] = React.useState(true)
  const [refreshing, setRefreshing] = React.useState(false)

  const currentUserId = useContext(CurrentUserContext).currentUser?.id

  const [messages, updateMessages] = useImmer<{
    ids: { [id: string]: boolean }
    list: ChatBubbleProps[]
  }>({
    ids: {},
    list: [],
  })

  const newlyPersistedMessages = React.useCallback(
    (newMessages: Api_Chat.ChatMessage[]) => {
      updateMessages((draft) => {
        newMessages.forEach((m) => {
          if (m.channelId !== channelId) {
            return
          }
          const newValue = {
            message: m,
            isCurrentUser: m.createdBy === currentUserId,
            isSending: false,
          }
          if (draft.ids[m.id]) {
            const indexToReplace = draft.list.findIndex(
              (elt) => elt.message.id === m.id,
            )
            if (indexToReplace > -1) {
              draft.list[indexToReplace] = newValue
            }
          } else {
            draft.ids[m.id] = true
            draft.list.push(newValue)
          }
        })
        draft.list.sort(
          (a, b) =>
            new Date(b.message.createdAt).getTime() -
            new Date(a.message.createdAt).getTime(),
        )
      })
    },
    [currentUserId, updateMessages, channelId],
  )

  const loadMore = React.useCallback(
    async ({ before, after }: { before?: Date; after?: Date }) => {
      if (refreshing) {
        return
      }
      setRefreshing(true)
      try {
        const results = await api.listMessages({
          channelId,
          before,
          after,
        })
        newlyPersistedMessages(results)
        if (before && results.length < 10) {
          setCanLoadBefore(false)
        }
      } catch (err) {
        Logger.info('could not load')
        //TODO: mark error somewhere
      }
      setRefreshing(false)
    },
    [refreshing, channelId, newlyPersistedMessages],
  )

  React.useEffect(() => {
    loadMore({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [oldestMessageDate /*, mostRecentMessageDate*/] = React.useMemo(() => {
    const last = messages.list.length > 0 ? messages.list[0] : undefined
    const first =
      messages.list.length > 0
        ? messages.list[messages.list.length - 1]
        : undefined
    return [first?.message.createdAt, last?.message.createdAt]
  }, [messages.list])

  const loadMoreBefore = React.useCallback(() => {
    if (oldestMessageDate) {
      loadMore({ before: new Date(oldestMessageDate) })
    }
  }, [oldestMessageDate, loadMore])

  React.useEffect(() => {
    const listener = (messageData: any) => {
      Logger.debug('got a message from inside ChannelMessages', messageData)
      if (messageData) {
        newlyPersistedMessages([messageData])
      }
    }
    chatSocket.on('message', listener)
    return () => {
      chatSocket.off('message', listener)
    }
  }, [newlyPersistedMessages])

  const onNewMessageSent = React.useCallback(
    (newMessage: Api_Chat.ChatMessage) => {
      newlyPersistedMessages([newMessage])
    },
    [newlyPersistedMessages],
  )

  if (!currentUserId) {
    return <div>Not authenticated.</div>
  }

  return (
    <div
      className={classnames(
        'column is-flex is-flex-direction-column ml-4 mr-4',
        {
          'is-hidden-mobile': !isChannelConversation,
        },
      )}>
      <div className="" style={{ flex: 1, position: 'relative' }}>
        <div
          id="scrollableDiv"
          style={{
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column-reverse',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}>
          <InfiniteScroll
            dataLength={messages.list.length}
            next={loadMoreBefore}
            style={{
              display: 'flex',
              flexDirection: 'column-reverse',
              paddingTop: 24,
              paddingBottom: 24,
            }}
            inverse={true}
            hasMore={canLoadBefore}
            loader={<div className="loader is-loading" />}
            scrollableTarget="scrollableDiv">
            {messages.list.map((elt) => (
              <ChatBubble key={elt.message.id} {...elt} />
            ))}
          </InfiniteScroll>
        </div>
      </div>
      <InputBox
        channel={channelDetails.channel}
        onNewMessageSent={onNewMessageSent}
      />
    </div>
  )
}
